import { isRotationDefaultIncorrectUnreachable, isRotationFeedbackFullySubsumed } from './rotation.js';
import { isCompassDefaultIncorrectUnreachable, isCompassFeedbackFullySubsumed } from './compass.js';
import { isGpsDefaultIncorrectUnreachable, isGpsFeedbackFullySubsumed } from './gps.js';
import { isValueDefaultIncorrectUnreachable, isValueFeedbackFullySubsumed } from './numerical.js';
import { isSliderValueDefaultIncorrectUnreachable, isSliderValueFeedbackFullySubsumed } from './slider.js';
import { isTiltDefaultIncorrectUnreachable, isTiltFeedbackFullySubsumed } from './tilt.js';
/**
 * `readonly` in, mutable arrays out to the per-block predicates, which all declare mutable
 * parameters.
 *
 * Purely a type-level conversion, and worth saying so: the first draft of this comment claimed the
 * copy protected the caller's array from being sorted underneath them. It does not, because nothing
 * downstream mutates it — `mergeCoverageIntervals` copies before merging, `mergeIntervals` sorts
 * that copy, and it accumulates into fresh `{ ...iv }` objects. A mutation test that dropped the
 * copy altogether passed every assertion here, which is how the overclaim was caught.
 *
 * The parameters are `readonly` because this module genuinely does not write to them, and that is
 * the guarantee callers get. The copy is what makes that signature compatible with predicates
 * written before it.
 */
const own = (xs) => [...xs];
/**
 * Whether the accepted band plus every span-shaped targeted state between them claim every value in
 * the domain, leaving Default Incorrect nothing to catch.
 *
 * `acceptedRanges` is the block's accepted band(s). GPS reads only the first: a GPS answer is one
 * radius around one target, so a second range would be a caller error rather than a wider answer.
 */
export function valueDefaultIncorrectUnreachable(blockType, acceptedRanges, feedbackTriggers) {
    const triggers = own(feedbackTriggers);
    switch (blockType) {
        case 'numerical': return isValueDefaultIncorrectUnreachable(own(acceptedRanges), triggers);
        case 'slider': return isSliderValueDefaultIncorrectUnreachable(own(acceptedRanges), triggers);
        case 'rotation': return isRotationDefaultIncorrectUnreachable(own(acceptedRanges), triggers);
        case 'compass': return isCompassDefaultIncorrectUnreachable(own(acceptedRanges), triggers);
        case 'gps': return isGpsDefaultIncorrectUnreachable(acceptedRanges[0] ?? null, triggers);
        case 'tilt': return isTiltDefaultIncorrectUnreachable(own(acceptedRanges), triggers);
    }
}
/**
 * Whether this targeted state sits entirely inside the accepted band, so Correct always wins first
 * and the state can never be the verdict.
 *
 * Not the same question as the one above, and not its complement: a state can be unreachable because
 * Correct shadows it (here) or because a sibling targeted state got there first (that is coverage,
 * and it is what makes Default Incorrect unreachable).
 */
export function valueFeedbackSubsumedByCorrect(blockType, trigger, acceptedRanges) {
    switch (blockType) {
        case 'numerical': return isValueFeedbackFullySubsumed(trigger, own(acceptedRanges));
        case 'slider': return isSliderValueFeedbackFullySubsumed(trigger, own(acceptedRanges));
        case 'rotation': return isRotationFeedbackFullySubsumed(trigger, own(acceptedRanges));
        case 'compass': return isCompassFeedbackFullySubsumed(trigger, own(acceptedRanges));
        case 'gps': return isGpsFeedbackFullySubsumed(trigger, acceptedRanges[0] ?? null);
        case 'tilt': return isTiltFeedbackFullySubsumed(trigger, own(acceptedRanges));
    }
}
/** Whether a block type's reachability is interval arithmetic at all — the guard a caller needs
 * before either function above, since the option blocks answer this from their own modules. */
export function isValueRangeBlockType(blockType) {
    return blockType === 'numerical' || blockType === 'slider' || blockType === 'rotation'
        || blockType === 'compass' || blockType === 'gps' || blockType === 'tilt';
}
//# sourceMappingURL=value-block-reachability.js.map